/**
 * question-coaching.ts — Per-question coaching data from Supabase.
 *
 * Fetches all question_coaching rows for the current tenant and returns
 * them keyed by question_key, so any component can look up coaching
 * content for any question without an extra round-trip.
 *
 * Cache strategy: module-scoped map keyed by tenant_id, TTL 10 min.
 * Same pattern as useTenantReps.ts — no external query library needed.
 *
 * Schema (question_coaching table, tenant_id = current):
 *   question_key          text  — primary lookup
 *   tenant_id             int
 *   why_it_matters        text
 *   narrative             text
 *   best_practice_answers jsonb — array of { option_label, guidance, is_strong }
 *   recommendation        text
 *   is_active             boolean
 */

import { useEffect, useState } from "react";
import { supabase, getCurrentTenant } from "@/lib/session";

// ─── Types ─────────────────────────────────────────────────────────────────

export interface BestPracticeAnswer {
  option_label: string;
  guidance: string;
  is_strong: boolean;
}

export interface QuestionCoaching {
  question_key: string;
  tenant_id: number;
  why_it_matters: string | null;
  narrative: string | null;
  best_practice_answers: BestPracticeAnswer[] | null;
  recommendation: string | null;
}

export type QuestionCoachingMap = Record<string, QuestionCoaching>;

// ─── Module-level cache ────────────────────────────────────────────────────

const CACHE_TTL_MS = 10 * 60 * 1000;

interface CacheEntry {
  map: QuestionCoachingMap;
  fetchedAt: number;
}

const _cache = new Map<number, CacheEntry>();
const _inflight = new Map<number, Promise<QuestionCoachingMap>>();

// ─── Fetch function ────────────────────────────────────────────────────────

async function fetchQuestionCoaching(
  tenantId: number,
): Promise<QuestionCoachingMap> {
  const existing = _inflight.get(tenantId);
  if (existing) return existing;

  const p = (async () => {
    const sb = supabase();
    const { data, error } = await sb
      .from("question_coaching")
      .select(
        "question_key, tenant_id, why_it_matters, narrative, best_practice_answers, recommendation",
      )
      .eq("tenant_id", tenantId)
      .eq("is_active", true);

    if (error) throw error;

    const map: QuestionCoachingMap = {};
    for (const row of data ?? []) {
      map[row.question_key] = {
        question_key: row.question_key,
        tenant_id: row.tenant_id,
        why_it_matters: row.why_it_matters ?? null,
        narrative: row.narrative ?? null,
        best_practice_answers: Array.isArray(row.best_practice_answers)
          ? (row.best_practice_answers as BestPracticeAnswer[])
          : null,
        recommendation: row.recommendation ?? null,
      };
    }

    _cache.set(tenantId, { map, fetchedAt: Date.now() });
    return map;
  })();

  _inflight.set(tenantId, p);
  try {
    return await p;
  } finally {
    _inflight.delete(tenantId);
  }
}

// ─── Hook ──────────────────────────────────────────────────────────────────

export function useQuestionCoaching(): {
  coachingMap: QuestionCoachingMap;
  loading: boolean;
  error: Error | null;
} {
  const tenant = getCurrentTenant();
  const tenantId = tenant?.id ?? null;

  const cached = tenantId != null ? _cache.get(tenantId) : null;
  const fresh = cached != null && Date.now() - cached.fetchedAt < CACHE_TTL_MS;

  const [coachingMap, setCoachingMap] = useState<QuestionCoachingMap>(
    fresh ? cached!.map : {},
  );
  const [loading, setLoading] = useState<boolean>(!fresh && tenantId != null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (tenantId == null) {
      setCoachingMap({});
      setLoading(false);
      return;
    }
    const c = _cache.get(tenantId);
    if (c && Date.now() - c.fetchedAt < CACHE_TTL_MS) {
      setCoachingMap(c.map);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchQuestionCoaching(tenantId)
      .then((m) => {
        if (cancelled) return;
        setCoachingMap(m);
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        setCoachingMap({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  return { coachingMap, loading, error };
}

/** Clear the module cache (useful in tests or after admin edits). */
export function clearQuestionCoachingCache(tenantId?: number) {
  if (tenantId == null) _cache.clear();
  else _cache.delete(tenantId);
}
